import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Ban, Search, Plus, Trash2, Phone, User, Calendar,
  AlertTriangle, MessageSquare, RefreshCw, Download,
  ChevronLeft, ChevronRight, Shield
} from 'lucide-react';

const API = '/api';

export default function Blacklist() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [newEntry, setNewEntry] = useState({ phone: '', contact_name: '', notes: '' });
  const [bulkPhones, setBulkPhones] = useState('');

  const limit = 20;

  const fetchBlacklist = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit });
      if (search) params.append('search', search);
      
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/blacklist?${params}`),
        axios.get(`${API}/blacklist/stats`)
      ]);
      
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Error al cargar blacklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklist();
  }, [page, search]);

  const handleAdd = async () => {
    if (!newEntry.phone) {
      toast.error('El teléfono es requerido');
      return;
    }
    try {
      await axios.post(`${API}/blacklist`, newEntry);
      toast.success('Número añadido a blacklist');
      setShowAddModal(false);
      setNewEntry({ phone: '', contact_name: '', notes: '' });
      fetchBlacklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al añadir');
    }
  };

  const handleBulkAdd = async () => {
    const phones = bulkPhones
      .split('\n')
      .map(p => p.trim())
      .filter(p => p.length > 5);
    
    if (phones.length === 0) {
      toast.error('No se encontraron números válidos');
      return;
    }

    try {
      const res = await axios.post(`${API}/blacklist/bulk`, phones);
      toast.success(`${res.data.added} números añadidos de ${res.data.total_requested}`);
      setShowBulkModal(false);
      setBulkPhones('');
      fetchBlacklist();
    } catch (e) {
      toast.error('Error al añadir números');
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('¿Eliminar este número de la blacklist?')) return;
    try {
      await axios.delete(`${API}/blacklist/${id}`);
      toast.success('Número eliminado de blacklist');
      fetchBlacklist();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const exportBlacklist = () => {
    const csv = items.map(i => `${i.phone},${i.contact_name},${i.reason}`).join('\n');
    const blob = new Blob([`phone,name,reason\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blacklist.csv';
    a.click();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Ban className="w-7 h-7 text-red-500" />
            Blacklist
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Números bloqueados que no recibirán mensajes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkModal(true)}
            data-testid="blacklist-bulk-add-btn"
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Añadir Múltiples
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="blacklist-add-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Añadir Número
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-zinc-500">Total bloqueados</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <p className="text-2xl font-bold text-amber-500">{stats.auto_blacklisted}</p>
            </div>
            <p className="text-xs text-zinc-500">Auto-bloqueados (STOP)</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <p className="text-2xl font-bold text-blue-500">{stats.manual_blacklisted}</p>
            </div>
            <p className="text-xs text-zinc-500">Añadidos manualmente</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-2xl font-bold text-emerald-500">{stats.recent_week}</p>
            <p className="text-xs text-zinc-500">Últimos 7 días</p>
          </div>
        </div>
      )}

      {/* Search & Actions */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Buscar por teléfono o nombre..."
            data-testid="blacklist-search-input"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
          />
        </div>
        <button
          onClick={fetchBlacklist}
          className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          onClick={exportBlacklist}
          className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
        >
          <Download className="w-4 h-4" />
          Exportar
        </button>
      </div>

      {/* List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-zinc-700 border-t-blue-500 rounded-full mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <Ban className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No hay números en la blacklist</p>
            <p className="text-xs text-zinc-600 mt-1">
              Los números que respondan "STOP" o "BAJA" se añadirán automáticamente
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {items.map((item) => (
              <div
                key={item.id}
                className="p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">{item.phone}</p>
                      {item.reason === 'auto' && (
                        <span className="px-2 py-0.5 bg-amber-500/10 text-amber-500 text-[10px] font-medium rounded-full">
                          AUTO
                        </span>
                      )}
                    </div>
                    {item.contact_name && (
                      <p className="text-sm text-zinc-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.contact_name}
                      </p>
                    )}
                    {item.trigger_message && (
                      <p className="text-xs text-zinc-600 flex items-center gap-1 mt-1">
                        <MessageSquare className="w-3 h-3" />
                        "{item.trigger_message.substring(0, 50)}..."
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-xs text-zinc-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.created_at).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleRemove(item.id)}
                    data-testid={`blacklist-delete-${item.id}`}
                    className="p-2 text-zinc-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-zinc-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-zinc-500">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-zinc-400"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Single Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Añadir a Blacklist
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Teléfono *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="tel"
                    value={newEntry.phone}
                    onChange={(e) => setNewEntry({ ...newEntry, phone: e.target.value })}
                    data-testid="blacklist-modal-phone-input"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Nombre (opcional)
                </label>
                <input
                  type="text"
                  value={newEntry.contact_name}
                  onChange={(e) => setNewEntry({ ...newEntry, contact_name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
                  placeholder="Nombre del contacto"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Notas (opcional)
                </label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700 resize-none"
                  placeholder="Razón del bloqueo..."
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAdd}
                data-testid="blacklist-modal-submit-btn"
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors"
              >
                Añadir a Blacklist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Añadir Múltiples Números
            </h2>
            <div>
              <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                Números (uno por línea)
              </label>
              <textarea
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                data-testid="blacklist-bulk-textarea"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700 resize-none font-mono text-sm"
                placeholder={"+54 9 11 1234-5678\n+54 9 11 8765-4321\n..."}
                rows={8}
              />
              <p className="text-xs text-zinc-600 mt-2">
                {bulkPhones.split('\n').filter(p => p.trim().length > 5).length} números detectados
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkAdd}
                data-testid="blacklist-bulk-submit-btn"
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors"
              >
                Añadir Todos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
